/**
 * #3528 (LEG 3) — the journal total-order key `(t, device_id, seq, id)` carried
 * onto every journal-projected {@link ChatMessage}, and the comparator that
 * reproduces the substrate order (crucible-build-state `compareJournalEvents`).
 *
 * WHY this exists. The authoritative cold-heal deletes a journal-projected row
 * that a fresh complete snapshot no longer contains. Its old boundary compared
 * `ChatMessage.timestamp` (epoch ms) alone with `<=`, but the journal's total
 * order is `(t, device_id, seq, id)`: two rows can share the same millisecond
 * `t` yet be legitimately DISTINCT (a second device, an equal-ms sibling, a
 * higher `seq`). An absent-but-equal-ms row then satisfied `ts <= watermark`
 * and was wrongly deleted. Comparing the FULL order key with a STRICT `<`
 * boundary makes an equal-ms row from another device / higher seq sort AFTER the
 * watermark (preserved), while a genuinely-superseded lower-order-key row still
 * sorts strictly within the covered window (deleted).
 *
 * Pure + dependency-free leaf module so BOTH the projection
 * (`project-agent-history.ts`) and the reducer (`state.ts`) can import it
 * without reintroducing the state.ts ↔ project-agent-history.ts runtime cycle.
 * The comparator MIRRORS `compareJournalEvents` in `@volley/crucible-build-state`
 * exactly (string compare on `t`, then `device_id`, numeric `seq`, string `id`);
 * crucible-ui does not depend on that runtime package, so it is re-stated here.
 */

/** The four-component journal total-order key. `t` is the raw ISO string (NOT
 *  the epoch-ms number) so the primary comparison is byte-identical to the
 *  substrate's `a.t < b.t` string compare. */
export interface JournalOrderKey {
  readonly t: string;
  readonly deviceId: string;
  readonly seq: number;
  readonly id: string;
}

/** Compare two order keys by the substrate total order `(t, device_id, seq,
 *  id)`. Returns <0 / 0 / >0 like `Array.prototype.sort`'s comparator. */
export function compareJournalOrderKeys(a: JournalOrderKey, b: JournalOrderKey): number {
  if (a.t !== b.t) return a.t < b.t ? -1 : 1;
  if (a.deviceId !== b.deviceId) return a.deviceId < b.deviceId ? -1 : 1;
  if (a.seq !== b.seq) return a.seq - b.seq;
  if (a.id !== b.id) return a.id < b.id ? -1 : 1;
  return 0;
}
